import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { chain, reduce } from 'lodash'

import { observer } from 'mobx-react'

import RadioInput from 'modules/core/components/Inputs/FilterSelect/SelectOption/RadioInput'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import formatCurrency from 'modules/core/lib/formatCurrency'
import { currencyToNumber } from 'modules/core/lib/formatCurrencyUtils'
import i18n from 'modules/core/lib/i18n'

import { usePayrollBundlePageContext } from 'modules/payroll_bundle/components/PayrollBundle'
import { useDraftPaymentItems } from 'modules/payroll_bundle/hooks/useDraftPaymentItems'
import { useFetchResultData } from 'modules/payroll_bundle/hooks/useFetchResultData'
import { SELECT_DATA, UPDATE_DATA } from 'modules/payroll_bundle/lib/constants'
import { ResultConceptsType, resultConceptsLabels } from 'modules/payroll_bundle/lib/constants'
import { usePayrollPayments } from 'modules/payroll_bundle/lib/payrollPaymentsContext'
import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'

interface Concept {
  type: ResultConceptsType
  count: number
  amount: number
}

interface ConceptSelectOptionProps {
  concept: Concept
  onClick: () => void
}

const ConceptSelectOption = ({ concept, onClick }: ConceptSelectOptionProps) => {
  const { paymentConceptType } = usePayrollPayments()
  return (
    <Box
      key={concept.type}
      border={{
        all: {
          width: 's2',
          style: 'solid',
          color: concept.type == paymentConceptType ? 'viridian1000' : 'grey400',
        },
      }}
      borderRadius={{ all: 'abs008' }}
      padding="s16"
      onClick={() => {
        onClick()
      }}
    >
      <Box gap="s8" flexDirection="row" justifyContent="spaceBetween">
        <Box>
          <Text.Normal weight="medium">{resultConceptsLabels()[concept.type]}</Text.Normal>
          <Text.Normal>
            {concept.count} {i18n.tp('common.employee_noun', { count: concept.count })} &middot;{' '}
            {formatCurrency(currencyToNumber(concept.amount))}
          </Text.Normal>
        </Box>
        <Box>
          <RadioInput selected={concept.type == paymentConceptType} />
        </Box>
      </Box>
    </Box>
  )
}

const PaymentConceptSelectionModal = () => {
  const navigate = useNavigate()
  const { navigateTo } = usePayrollBundlePageContext()

  const { isLoading } = useFetchResultData()
  const { paymentDispatch } = usePayrollPayments()
  const { currentPeriod } = usePolicyPeriods()
  const { loading: loadingDraftPaymentItems, data: paymentData } = useDraftPaymentItems(
    currentPeriod?.id
  )

  const paymentDataByConcept: Array<Concept> = chain(paymentData)
    .groupBy((payment) => {
      return payment.paymentType
    })
    .map((group, key) => {
      return {
        type: key as ResultConceptsType,
        count: group.length,
        amount: reduce(group, (sum, item) => sum + item.amountInCents, 0),
      }
    })
    .value()

  const setSelectedConceptType = ({ conceptType }: { conceptType: ResultConceptsType }) => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        paymentConceptType: conceptType,
      },
    })
  }

  const onClose = () => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        showSelectConceptModal: false,
      },
    })
  }

  const onSubmit = () => {
    paymentDispatch({
      type: UPDATE_DATA,
      payload: {
        paymentStage: SELECT_DATA,
        showSelectConceptModal: false,
      },
    })

    if (navigateTo) {
      navigate(navigateTo)
    }
  }

  return (
    <Modal
      illustration="table-file"
      title={i18n.t('payroll_bundle.results_payments.payments.concept_modal.title')}
      description={i18n.t('payroll_bundle.results_payments.payments.concept_modal.description')}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Box flexDirection="row" justifyContent="flexEnd" width="full" gap="s8">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={false} submit>
            {i18n.t('common.continue')}
          </Button>
        </Box>
      }
    >
      {isLoading || loadingDraftPaymentItems ? (
        <Loading />
      ) : (
        <Box gap="s8">
          {paymentDataByConcept.map((concept) => (
            <ConceptSelectOption
              key={concept.type}
              concept={concept}
              onClick={() => {
                setSelectedConceptType({ conceptType: concept.type })
              }}
            />
          ))}
        </Box>
      )}
    </Modal>
  )
}

export default observer(PaymentConceptSelectionModal)
